import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Text, Input } from '@bonanza-social/moonlight';
import {
  AddressFieldProps,
  AddressInfo,
  RetrieveAddressResponse,
} from './AddressField.types';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { useTranslation } from '@bonanza-social/i18n';
import { BottomSheet, SelectableOptionItem } from '@bonanza-social/navigation';
import {
  useDebouncedAddressSearch,
  useValidateManualFields,
} from '../../hooks';
import ManualFieldInput from '../ManualFieldInput/ManualFieldInput';
import { Pressable, TextInput } from 'react-native';
import { retrieveAddress } from '@bonanza-social/toolkit/api';
import { ADDRESS_FIELDS } from './AddressField.content';

function AddressField({
  fullAddress,
  defaultAddress = '',
  province = '',
  focusFirstInputField = false,
  setFocusFirstInputField,
  onAddressInfoChange,
}: AddressFieldProps) {
  const dispatch = useDispatch();
  const inputRefs = useRef<(TextInput | null)[]>([]);

  const { t } = useTranslation('detailsAttestation');
  const [addressInput, setAddressInput] = useState<string>(
    fullAddress?.Line1 || defaultAddress,
  );

  const [addressInputError, setAddressInputError] = useState<string>('');
  const [manualAddress, setManualAddress] = useState<boolean>(false);
  const [manualFields, setManualFields] = useState<Partial<AddressInfo>>({
    unit: fullAddress?.SubBuilding,
    building: fullAddress?.BuildingNumber,
    street: fullAddress?.Street,
    city: fullAddress?.City,
    postalCode: fullAddress?.PostalCode,
  });

  useEffect(() => {
    if (manualAddress && focusFirstInputField) {
      inputRefs.current[0]?.focus();
      setFocusFirstInputField?.(false);
    }
  }, [manualAddress, focusFirstInputField, setFocusFirstInputField]);

  const { performDebouncedAddressSearch, cancelSearch } =
    useDebouncedAddressSearch();

  const validationRules = useMemo(
    () => ({
      postalCode: {
        regex:
          /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/,
        errorKey: 'invalidPostalCode',
      },
    }),
    [],
  );

  const { errors, validateManualFields } = useValidateManualFields(
    manualFields,
    t,
    ['unit'],
    validationRules,
  );

  const validateAddressInput = useCallback(() => {
    if (!addressInput.trim()) {
      setAddressInputError(t('required'));
      return;
    }
    setAddressInputError('');
  }, [addressInput, t]);

  const handleSubmitEditing = useCallback((currentIndex: number) => {
    if (currentIndex < ADDRESS_FIELDS.length - 1) {
      inputRefs.current[currentIndex + 1]?.focus();
    }
  }, []);

  const handleSuggestionSelect = useCallback(
    async (selectableOptionItem: SelectableOptionItem) => {
      if (!selectableOptionItem) {
        setAddressInput('');
        setManualFields({
          unit: '',
          building: '',
          street: '',
          city: '',
          postalCode: '',
        });
      } else {
        const { payload: retrievedFullAddress } = await dispatch(
          retrieveAddress(selectableOptionItem.dynamicValues?.Id || ''),
        );

        const addressData = retrievedFullAddress as RetrieveAddressResponse;

        if (addressData?.Items?.[0]) {
          const {
            PostalCode = '',
            Line1 = '',
            City = '',
            SubBuilding = '',
            BuildingNumber = '',
            Street = '',
            Province = '',
          } = addressData.Items[0];

          const formattedAddress = `${Line1}, ${City}, ${province ?? Province}, ${PostalCode}`;
          setAddressInput(formattedAddress);
          setManualFields({
            unit: SubBuilding,
            building: BuildingNumber,
            street: Street,
            city: City,
            postalCode: PostalCode,
          });
        }
      }
    },
    [province, dispatch],
  );

  useEffect(() => {
    setManualFields({
      unit: fullAddress?.SubBuilding,
      building: fullAddress?.BuildingNumber,
      street: fullAddress?.Street,
      city: fullAddress?.City,
      postalCode: fullAddress?.PostalCode,
    });
  }, [fullAddress]);

  useEffect(() => {
    const { unit, building, street, city, postalCode } = manualFields;

    const nonEmptyFields = [
      unit,
      building,
      street ? `${street},` : '',
      city ? `${city},` : '',
      province ? `${province},` : '',
      postalCode,
    ].join(' ');

    if (nonEmptyFields) {
      setAddressInput(nonEmptyFields);
    }
  }, [manualFields, province, setAddressInput]);

  const onAddressPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'AddressSearch',
      data: {
        headerNS: 'detailsAttestation',
        headerKey: 'address',
        buttonNS: 'detailsAttestation',
        buttonKey: 'saveButton',
        maxHeight: '30%',
        centerContent: true,
        fetchAddresses: performDebouncedAddressSearch,
        defaultValue: addressInput,
        onSelect: handleSuggestionSelect,
      },
    });
  }, [handleSuggestionSelect, performDebouncedAddressSearch, addressInput]);

  const toggleManualAddress = useCallback(() => {
    setManualAddress((prev) => !prev);
    setFocusFirstInputField?.(false);

    cancelSearch();
  }, [setFocusFirstInputField, cancelSearch]);

  const handleManualFieldChange = useCallback(
    (field: string, value: string) => {
      setManualFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  useEffect(() => {
    onAddressInfoChange(
      {
        address: addressInput,
        building: manualFields.building,
        unit: manualFields.unit,
        street: manualFields.street,
        city: manualFields.city,
        postalCode: manualFields.postalCode,
      },
      errors as AddressInfo,
    );
  }, [manualFields, addressInput, onAddressInfoChange, errors]);

  useEffect(() => {
    validateManualFields();
  }, [validateManualFields]);

  useEffect(() => {
    validateAddressInput();
  }, [addressInput, validateAddressInput]);

  return (
    <Box>
      {!manualAddress ? (
        <Box marginBottom="space-400">
          <Pressable onPress={onAddressPressIn}>
            <Input
              value={addressInput}
              placeholderNS="detailsAttestation"
              placeholderKey="address"
              placeholder={t('addressInputPlaceholder')}
              editable={false}
              error={addressInputError}
              onPressIn={onAddressPressIn}
            />
          </Pressable>
          {addressInputError && (
            <Text color="darkSurfaceError" variant="body-m">
              {addressInputError}
            </Text>
          )}
        </Box>
      ) : (
        <Box>
          {ADDRESS_FIELDS.map(({ field, placeholderKey }, index) => (
            <Box key={field} marginBottom="space-400">
              <ManualFieldInput
                key={field}
                ref={(el) => (inputRefs.current[index] = el)}
                field={field}
                value={manualFields[field]}
                enterKeyHint="next"
                onSubmitEditing={() => handleSubmitEditing(index)}
                submitBehavior={
                  index === ADDRESS_FIELDS.length - 1
                    ? 'blurAndSubmit'
                    : 'submit'
                }
                placeholderNS="detailsAttestation"
                placeholderKey={placeholderKey}
                error={errors[field]}
                onChange={handleManualFieldChange}
              />
            </Box>
          ))}
        </Box>
      )}
      <Box>
        <Text
          variant="link-small"
          color="darkSurfacePrimary"
          onPress={toggleManualAddress}
          locNS="detailsAttestation"
          locKey={manualAddress ? 'switchToAuto' : 'enterManually'}
        />
      </Box>
    </Box>
  );
}

export default memo(AddressField);
